import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageLayout from "../../../../../components/PageLayout";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { downloadPdf } from "../../../../../hooks/use-download-pdf";
import { useQueryParams } from "../../../../../hooks/use-query-param";
import { SeverityRange } from "../../../../../queries/dashboard/use-dashboard-patient-questionaire";
import { usePatientAssessmentExport } from "../../../../../queries/patient-chart/assessment/use-patient-assessment-export";
import { formatDateTime } from "../../../../../utils/date-time-utility";
// import { SECTION_GAP } from "../../../../../components/app/provider/patient/chart/tokens";
import {
  SEND_SCHEDULED_SUB_TABS,
  SUBMITTED_SUB_TABS,
} from "./AssessmentConstant";
import AssessmentSendScheduled from "./AssessmentSendScheduled";
import AssessmentSubmitted from "./AssessmentSubmitted";
import AssessmentTopBar from "./AssessmentTopBar";
import { AssessmentFilterData } from "./AssessmentsList";

const Assessments = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeAssessment = searchParams.get("activeAssessment") || "submitted";
  const activeSubTabParam = searchParams.get("activeSubTab");

  // Determine default sub-tab based on main tab
  const getDefaultSubTab = (mainTab: string) => {
    if (mainTab === "submitted") {
      return activeSubTabParam || SUBMITTED_SUB_TABS[0].value;
    } else if (mainTab === "sent_scheduled") {
      return activeSubTabParam || SEND_SCHEDULED_SUB_TABS[0].value;
    }
    return activeSubTabParam || SUBMITTED_SUB_TABS[0].value;
  };

  const [activeTab, setActiveTab] = useState(activeAssessment);
  const [activeSubTab, setActiveSubTab] = useState(
    getDefaultSubTab(activeAssessment)
  );

  const severity = useQueryParams("severity");

  const currentSeverity = (severity: SeverityRange) => {
    const SEVERITY_OPTIONS = {
      LOW: { label: "Low", value: "LOW" },
      MODERATE: { label: "Moderate", value: "MODERATE" },
      HIGH: { label: "High", value: "HIGH" },
      VERY_LOW: { label: "Very Low", value: "VERY_LOW" },
      VERY_HIGH: { label: "Very High", value: "VERY_HIGH" },
    };

    return SEVERITY_OPTIONS[severity];
  };

  const [assessmentFilter, setAssessmentFilter] =
    useState<AssessmentFilterData>({
      severity: severity
        ? currentSeverity(severity as SeverityRange)
        : undefined,
    });
  const [archive, setArchive] = useState<boolean>(false);
  const [isSendScheduleAssessment] = useState<boolean>(false);
  const [searchKey, setSearchKey, instantSearchKey] = useDebounce<string>("");
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  // Use refs to store assessments from child components
  const submittedAssessmentsRef = useRef<any[]>([]);
  const sendScheduledAssessmentsRef = useRef<any[]>([]);

  const { mutateAsync: exportAssessment } = usePatientAssessmentExport();

  const handleExportAssessment = (selected: "csv" | "pdf") => {
    let responseType = "application/pdf";
    if (selected === "csv") responseType = "text/csv";

    exportAssessment({
      exportType: selected,
      questionnaireIds: rowSelectionModel,
    }).then((resp) => {
      const blobFile = new Blob([resp.data], {
        type: responseType,
      });

      downloadPdf(
        blobFile,
        `Assessments.${formatDateTime(new Date())}.${selected.toLowerCase()}`
      );
    });
  };

  // Get current assessments list based on active tab
  const getCurrentAssessments = () => {
    if (activeTab === "submitted") {
      return submittedAssessmentsRef.current;
    } else {
      return sendScheduledAssessmentsRef.current;
    }
  };

  const handleSetSearchParams = (params: {
    activeAssessment?: string;
    activeSubTab?: string;
  }) => {
    const newParams = new URLSearchParams(searchParams);
    if (params.activeAssessment) {
      newParams.set("activeAssessment", params.activeAssessment);
    }
    if (params.activeSubTab) {
      newParams.set("activeSubTab", params.activeSubTab);
    }
    setSearchParams(newParams);
  };

  return (
    <PageLayout
      header={
        <AssessmentTopBar
          activeAssessment={activeTab}
          setActiveAssessment={setActiveTab}
          setSearchParams={handleSetSearchParams}
          setFilters={setAssessmentFilter}
          filters={assessmentFilter}
          setSearchStringKey={setSearchKey}
          searchStringKey={instantSearchKey}
          isSendScheduleAssessment={isSendScheduleAssessment}
          rowSelectionModel={rowSelectionModel}
          assessments={getCurrentAssessments()}
          onExportAssessment={handleExportAssessment}
          activeSubTab={activeSubTab}
          setActiveSubTab={setActiveSubTab}
          setRowSelectionModel={setRowSelectionModel}
          setArchive={setArchive}
          archive={archive}
        />
      }
    >
      {activeTab === "submitted" ? (
        <AssessmentSubmitted
          filters={assessmentFilter}
          searchKey={searchKey}
          rowSelectionModel={rowSelectionModel}
          setRowSelectionModel={setRowSelectionModel}
          onExportAssessment={handleExportAssessment}
          onAssessmentsChange={(assessments) => {
            submittedAssessmentsRef.current = assessments;
          }}
          reviewStatus={activeSubTab as "pending_review" | "reviewed"}
        />
      ) : (
        <AssessmentSendScheduled
          archive={archive}
          filters={assessmentFilter}
          searchKey={searchKey}
          onAssessmentsChange={(assessments) => {
            sendScheduledAssessmentsRef.current = assessments;
          }}
          scheduledStatus={activeSubTab as "sent" | "scheduled"}
        />
      )}
    </PageLayout>
  );
};

export default Assessments;
